import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";

type Policy = {
  id: string;
  action: string;
  scope: string;
  match_value?: string | null;
  window: "hour" | "day";
  limit_count: number;
  is_enabled: boolean;
  priority: number;
  description?: string | null;
  meta_json?: Record<string, unknown>;
};

type PolicyDraft = {
  action: string;
  scope: string;
  match_value: string;
  window: "hour" | "day";
  limit_count: number;
  is_enabled: boolean;
  priority: number;
  description: string;
};

const EMPTY_POLICY: PolicyDraft = {
  action: "signup_start",
  scope: "ip",
  match_value: "",
  window: "hour" as const,
  limit_count: 30,
  is_enabled: true,
  priority: 100,
  description: "",
};

export function SystemAbuseLimitsPage() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<PolicyDraft>(EMPTY_POLICY);
  const [editingId, setEditingId] = useState<string>("");
  const [preview, setPreview] = useState({ action: "signup_start", ip: "127.0.0.1", geo_country_code: "US", selected_country_code: "US", locale: "en", telegram_user_id: "" });

  const list = useQuery({ queryKey: ["sys-abuse-limits"], queryFn: () => apiGet<{ items: Policy[] }>("/v3/sys/abuse/limits") });
  const previewQuery = useQuery({
    queryKey: ["sys-abuse-preview", preview],
    queryFn: () => apiPost<{ blocked_by_blocklist: Record<string, unknown> | null; matched_policies: Array<Record<string, unknown>> }>("/v3/sys/abuse/limits/preview", {
      ...preview,
      telegram_user_id: preview.telegram_user_id || null,
    }),
  });

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = { ...draft, match_value: draft.match_value || null, meta_json: {} };
      return editingId ? apiPut(`/v3/sys/abuse/limits/${editingId}`, payload) : apiPost("/v3/sys/abuse/limits", payload);
    },
    onSuccess: async () => {
      setDraft(EMPTY_POLICY);
      setEditingId("");
      await qc.invalidateQueries({ queryKey: ["sys-abuse-limits"] });
      await qc.invalidateQueries({ queryKey: ["sys-abuse-preview"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/sys/abuse/limits/${id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["sys-abuse-limits"] });
      await qc.invalidateQueries({ queryKey: ["sys-abuse-preview"] });
    },
  });

  const sorted = useMemo(() => (list.data?.items ?? []).slice().sort((a, b) => a.priority - b.priority), [list.data]);

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">Rate Limit Policies</h1>
      <section className="panel p-4 grid gap-2">
        <h2 className="text-lg font-semibold">{editingId ? "Edit Policy" : "Create Policy"}</h2>
        <div className="grid md:grid-cols-4 gap-2">
          <input className="input" value={draft.action} onChange={(e) => setDraft({ ...draft, action: e.target.value })} placeholder="action" />
          <input className="input" value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })} placeholder="scope" />
          <input className="input" value={draft.match_value} onChange={(e) => setDraft({ ...draft, match_value: e.target.value })} placeholder="match_value (optional)" />
          <select className="input" value={draft.window} onChange={(e) => setDraft({ ...draft, window: e.target.value as "hour" | "day" })}><option value="hour">hour</option><option value="day">day</option></select>
        </div>
        <div className="grid md:grid-cols-4 gap-2">
          <input className="input" type="number" value={draft.limit_count} onChange={(e) => setDraft({ ...draft, limit_count: Number(e.target.value) || 1 })} placeholder="limit" />
          <input className="input" type="number" value={draft.priority} onChange={(e) => setDraft({ ...draft, priority: Number(e.target.value) || 100 })} placeholder="priority" />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={draft.is_enabled} onChange={(e) => setDraft({ ...draft, is_enabled: e.target.checked })} /> enabled</label>
          <button className="btn" onClick={() => saveMutation.mutate()} disabled={!draft.action || !draft.scope}>{editingId ? "Update" : "Create"}</button>
        </div>
        <input className="input" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="description" />
      </section>

      <section className="panel p-4 grid gap-2">
        <h2 className="text-lg font-semibold">Effective Policy Preview</h2>
        <div className="grid md:grid-cols-3 gap-2">
          <input className="input" value={preview.action} onChange={(e) => setPreview({ ...preview, action: e.target.value })} placeholder="action" />
          <input className="input" value={preview.ip} onChange={(e) => setPreview({ ...preview, ip: e.target.value })} placeholder="ip" />
          <input className="input" value={preview.telegram_user_id} onChange={(e) => setPreview({ ...preview, telegram_user_id: e.target.value })} placeholder="telegram_user_id" />
          <input className="input" value={preview.geo_country_code} onChange={(e) => setPreview({ ...preview, geo_country_code: e.target.value.toUpperCase() })} placeholder="geo_country_code" />
          <input className="input" value={preview.selected_country_code} onChange={(e) => setPreview({ ...preview, selected_country_code: e.target.value.toUpperCase() })} placeholder="selected_country_code" />
          <input className="input" value={preview.locale} onChange={(e) => setPreview({ ...preview, locale: e.target.value.toLowerCase() })} placeholder="locale" />
        </div>
        {previewQuery.error ? <p className="text-sm text-rose-600">{String(previewQuery.error)}</p> : null}
        <pre className="bg-slate-900 text-slate-100 rounded p-3 overflow-auto text-xs">{JSON.stringify(previewQuery.data ?? {}, null, 2)}</pre>
      </section>

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">Policies</h2>
        <div className="mt-2 grid gap-2 text-sm">
          {sorted.map((row) => (
            <div key={row.id} className="rounded border border-slate-200 p-3 bg-white grid gap-2">
              <div className="flex flex-wrap gap-2 items-center justify-between">
                <div>
                  <strong>{row.action}</strong> / {row.scope} / {row.window} / limit {row.limit_count}
                  <span className="ml-2 text-slate-500">priority {row.priority}</span>
                  {row.match_value ? <span className="ml-2 text-slate-500">match {row.match_value}</span> : null}
                </div>
                <div className="flex gap-2">
                  <button
                    className="btn secondary"
                    onClick={() => {
                      setEditingId(row.id);
                      setDraft({
                        action: row.action,
                        scope: row.scope,
                        match_value: row.match_value ?? "",
                        window: row.window,
                        limit_count: row.limit_count,
                        is_enabled: row.is_enabled,
                        priority: row.priority,
                        description: row.description ?? "",
                      });
                    }}
                  >
                    Edit
                  </button>
                  <button className="btn" onClick={() => deleteMutation.mutate(row.id)}>Delete</button>
                </div>
              </div>
              {row.description ? <div className="text-slate-600">{row.description}</div> : null}
            </div>
          ))}
          {!sorted.length ? <p className="text-slate-500">No policies yet.</p> : null}
        </div>
      </section>
    </div>
  );
}
